/**
 * Signed-in pages. Without a session — the sign-in screen; when the session
 * ends while the user is in the app (any request answers 401) — the sign-in
 * screen with «Сесія завершилась», and after signing in «Об'єкти».
 * `only="operator"` pages are the administrator's; a client sees «Сторінку
 * не знайдено». The administrator's «Об'єкти» is the list of all objects.
 */
import { type ReactNode, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useProfile } from '../hooks/use-profile.js';
import { useT } from '../i18n/locale-context.js';
import { ApiError } from '../lib/api-client.js';
import { queryClient } from '../lib/query-client.js';
import { NotFoundPage } from '../pages/app/NotFoundPage.js';
import { clearSigningOut, isSigningOut } from './session.js';

export interface LoginState {
  expired?: boolean;
  reset?: boolean;
}

function useSessionWatch() {
  const navigate = useNavigate();
  useEffect(() => {
    const on401 = (err: unknown) => {
      if (!(err instanceof ApiError) || err.status !== 401 || isSigningOut()) return;
      queryClient.setQueryData(['profile'], null);
      navigate('/login', { replace: true, state: { expired: true } satisfies LoginState });
    };
    const offQueries = queryClient.getQueryCache().subscribe((e) => {
      if (e.type === 'updated' && e.action.type === 'error') on401(e.action.error);
    });
    const offMutations = queryClient.getMutationCache().subscribe((e) => {
      if (e.type === 'updated' && e.action.type === 'error') on401(e.action.error);
    });
    return () => {
      offQueries();
      offMutations();
    };
  }, [navigate]);
}

export function RequireAuth({ children, only }: { children: ReactNode; only?: 'operator' | 'client' }) {
  const t = useT();
  const { data, isLoading } = useProfile();
  const hadSession = useRef(false);
  useSessionWatch();

  if (data) {
    hadSession.current = true;
    clearSigningOut();
  }
  if (isLoading) {
    return (
      <div className="page">
        <p className="loading">{t('common.loading')}</p>
      </div>
    );
  }
  if (!data) {
    const state: LoginState | undefined =
      hadSession.current && !isSigningOut() ? { expired: true } : undefined;
    return <Navigate to="/login" replace state={state} />;
  }
  if (only === 'operator' && data.role !== 'operator') return <NotFoundPage />;
  if (only === 'client' && data.role === 'operator') return <Navigate to="/app/admin/sites" replace />;
  return <>{children}</>;
}
